// The Slaughters table's column-visibility toggle: which of its mutable columns are offered at all,
// and which of those default on. Pure, no Vue dependency - same shape as `referenceWindow.ts`. The
// generic mechanics (storage, toggling, select-all) live in the shared `useColumnVisibilityOverrides`
// composable instead - see its module doc - since only the availability/default rules below are
// genuinely specific to this page.

import type { SlaughterRow } from '@/types';

/** The columns a reader can show or hide. Fixed columns (date/location/number) are never in here. */
export type SlaughterToggleColumnId =
    | 'total_live_weight'
    | 'avg_live_weight'
    | 'total_slaughter_weight'
    | 'avg_slaughter_weight'
    | 'avg_meat_percent'
    | 'discarded_pigs'
    | 'avg_discarded_weight'
    | 'payment_weight'
    | 'section'
    | 'customer'
    | 'invoice'
    | 'classification'
    | 'settlement_no'
    | 'supplier_no'
    | 'created_by';

/**
 * Left-to-right table order of the mutable columns: the two weight pairs and average meat
 * percentage, then Discarded pigs and its average, then Payment weight, then the six presence-driven
 * facts.
 */
export const TOGGLE_COLUMN_IDS: readonly SlaughterToggleColumnId[] = [
    'total_live_weight',
    'avg_live_weight',
    'total_slaughter_weight',
    'avg_slaughter_weight',
    'avg_meat_percent',
    'discarded_pigs',
    'avg_discarded_weight',
    'payment_weight',
    'section',
    'customer',
    'invoice',
    'classification',
    'settlement_no',
    'supplier_no',
    'created_by',
];

/**
 * The columns whose availability - not just their default state - depends on the loaded rows: one
 * with nothing to show in any of them is dropped from the toggle list entirely, rather than merely
 * defaulting off. Every other column stays offered regardless of data, so a reader can turn one on
 * ahead of data showing up for it - see `defaultColumnVisible` for how presence steers their default
 * instead.
 */
const GATED_COLUMN_IDS: ReadonlySet<SlaughterToggleColumnId> = new Set(['section', 'customer', 'invoice', 'classification']);

export type GatedColumnId = 'section' | 'customer' | 'invoice' | 'classification';

/** Whether each toggleable column has at least one non-null entry among the given rows. */
export function columnPresence(rows: SlaughterRow[]): Record<SlaughterToggleColumnId, boolean> {
    const present = (pick: (row: SlaughterRow) => unknown) => rows.some((row) => pick(row) !== null);

    return {
        total_live_weight: present((row) => row.total_live_weight),
        avg_live_weight: present((row) => row.avg_live_weight),
        total_slaughter_weight: present((row) => row.total_slaughter_weight),
        avg_slaughter_weight: present((row) => row.avg_slaughter_weight),
        avg_meat_percent: present((row) => row.avg_meat_percent),
        discarded_pigs: present((row) => row.discarded_pigs),
        avg_discarded_weight: present((row) => row.avg_discarded_weight),
        payment_weight: present((row) => row.payment_weight),
        section: present((row) => row.section),
        customer: present((row) => row.customer),
        invoice: present((row) => row.invoice),
        classification: present((row) => row.classification),
        settlement_no: present((row) => row.settlement_no),
        supplier_no: present((row) => row.supplier_no),
        created_by: present((row) => row.created_by),
    };
}

/**
 * A column's default, before any stored override: on once a loaded row actually carries it, off
 * otherwise - except Created by, which is always off. The four hard-gated facts never reach this question with no data - they're unavailable
 * long before their default matters (see `isColumnAvailable`) - so in practice this only decides the
 * remaining columns' first-look state.
 */
export function defaultColumnVisible(columnId: SlaughterToggleColumnId, presence: Record<SlaughterToggleColumnId, boolean>): boolean {
    // Who registered a slaughter is audit detail, not something a reader wants by default.
    if (columnId === 'created_by') {
        return false;
    }

    return presence[columnId];
}

/** A column is offered in the dropdown at all - the four hard-gated facts only once the loaded rows
 * carry them; every other column is always offered (see `defaultColumnVisible` for how presence
 * steers their default instead). */
export function isColumnAvailable(columnId: SlaughterToggleColumnId, presence: Record<GatedColumnId, boolean>): boolean {
    return GATED_COLUMN_IDS.has(columnId) ? presence[columnId as GatedColumnId] : true;
}
